import cytoscape from 'cytoscape';
import fcose from 'cytoscape-fcose';
import cola from 'cytoscape-cola';
import dagre from 'cytoscape-dagre';
import expandCollapse from 'cytoscape-expand-collapse';
import { perfMark, perfMeasure, perfCount, isPerfLogEnabled } from 'utils/graph-perf';
import { sanitizeWorkerDataForLayout } from './graph-worker-serialization';

// Gated debug logger (audit H20). Only emits when the user passed
// `?perfLog=true`. Quiet by default so profiling traces stay clean.
const debugLog = (...args) => { if (isPerfLogEnabled()) console.debug(...args); };

// Register extensions once
cytoscape.use(fcose);
cytoscape.use(cola);
cytoscape.use(dagre);
cytoscape.use(expandCollapse);

// Minimum zoom we allow after an automatic fit. Prevents layouts that
// spread nodes across a huge coordinate range from forcing the viewport
// into unreadably tiny dots.
const MIN_FIT_ZOOM = 0.12;
// Zoom to use when exactly one visible node is present.
const SINGLE_NODE_ZOOM = 1;

/**
 * Named layout presets available in the UI.
 */
export const LAYOUT_PRESETS = {
  targetRadial: {
    label: 'Target-Radial',
    icon: '🎯',
    options: {
      name: 'concentric',
      animate: true,
      animationDuration: 500,
      minNodeSpacing: 40,
      padding: 50,
      startAngle: (3 / 2) * Math.PI,
      // concentric and levelWidth are injected at runtime via BFS from target node
    },
  },
  fcose: {
    label: 'Force-Directed',
    icon: '⚡',
    options: {
      name: 'fcose',
      animate: true,
      animationDuration: 500,
      randomize: true,
      nodeRepulsion: 45000,
      idealEdgeLength: 200,
      edgeElasticity: 0.45,
      gravity: 0.1,
      gravityRange: 3.8,
      numIter: 500,
    },
  },
  cola: {
    label: 'Constraint',
    icon: '🔗',
    options: {
      name: 'cola',
      animate: true,
      maxSimulationTime: 1500,
      nodeSpacing: 30,
      edgeLength: 150,
      convergenceThreshold: 0.01,
    },
  },
  dagre: {
    label: 'Hierarchy',
    icon: '🏗️',
    options: {
      name: 'dagre',
      animate: true,
      rankDir: 'TB',
      nodeSep: 50,
      rankSep: 100,
      edgeSep: 10,
    },
  },
  circle: {
    label: 'Circle',
    icon: '⭕',
    options: {
      name: 'circle',
      animate: true,
      animationDuration: 500,
      padding: 30,
    },
  },
  concentric: {
    label: 'Concentric',
    icon: '🎯',
    options: {
      name: 'concentric',
      animate: true,
      animationDuration: 500,
      minNodeSpacing: 60,
      concentric: (node) => node.degree(),
      levelWidth: () => 2,
    },
  },
  breadthfirst: {
    label: 'Tree',
    icon: '🌳',
    options: {
      name: 'breadthfirst',
      animate: true,
      animationDuration: 500,
      directed: false,
      spacingFactor: 1.5,
    },
  },
  grid: {
    label: 'Grid',
    icon: '▦',
    options: {
      name: 'grid',
      animate: true,
      animationDuration: 500,
      condense: true,
      rows: undefined,
      sort: (a, b) => {
        const typeOrder = { person: 0, org: 1, fund: 2, education: 3, engagement: 4 };
        const aType = typeOrder[a.data('vertexType')] ?? 5;
        const bType = typeOrder[b.data('vertexType')] ?? 5;
        if (aType !== bType) return aType - bType;
        return (a.data('label') || '').localeCompare(b.data('label') || '');
      },
    },
  },
  cose: {
    label: 'Spring',
    icon: '🌀',
    options: {
      name: 'cose',
      animate: true,
      animationDuration: 500,
      nodeRepulsion: 5000,
      idealEdgeLength: 100,
      gravity: 0.5,
    },
  },
};

/**
 * Layouts that can be offloaded to the Web Worker (headless Cytoscape).
 * Cola is excluded because it requires a DOM context.
 */
const WORKER_SUPPORTED_LAYOUTS = new Set([
  'fcose', 'cose', 'dagre', 'circle', 'concentric', 'grid', 'breadthfirst',
]);

/**
 * GraphAdapter — Performance-optimized abstraction layer over Cytoscape.js.
 *
 * Key improvements over v1:
 * - setElements() diffs incoming vs current elements (add/remove/update only changes)
 * - runLayout() applies graph-size guards (reduced iterations for large graphs)
 * - Returns metadata from setElements() indicating whether topology changed
 * - Layout computation is offloaded to a Web Worker for supported layouts,
 *   keeping the main thread responsive during expensive force-directed runs.
 */
const ORIGIN = { x: 0, y: 0 };

/** Bound on the position cache; oldest entries are evicted first (S13). */
const POSITION_CACHE_LIMIT = 16;

class GraphAdapter {
  constructor() {
    this.cy = null;
    this.ecApi = null;
    this.currentLayout = null;
    this.initialLayoutDone = false;
    this._prevIncoming = new Map();

    /** @type {Worker|null} */
    this.layoutWorker = null;
    /** Monotonically-increasing generation counter for layout requests */
    this._layoutGeneration = 0;
    /** Whether the worker failed to initialize (permanent fallback to main thread) */
    this._workerFailed = false;
    /**
     * Last-sent worker state (Phase 12). For each id we track the data
     * shallow-equal hash + last position so subsequent runs can ship only
     * the delta. Cleared on `destroy()`.
     */
    this._workerState = new Map(); // id → { data, x, y }

    // Topology-signature layout cache (S13). Maps a signature derived from
    // the sorted set of (node id + parent + edge endpoints) to the position
    // map produced by the most recent layout for that topology. When a
    // subsequent layout request finds the same signature, positions are
    // restored from the cache instead of re-running layout.
    /** @type {Map<string, {[id: string]: {x:number,y:number}}>} */
    this._positionCache = new Map();
    /** @type {string|null} */
    this._lastSignature = null;
  }

  // ── Lifecycle ──────────────────────────────────────────────────────

  init(container, options = {}) {
    // Audit H8 / Phase 11 — Lower `pixelRatio` for big graphs. Cytoscape
    // sets pixelRatio at construction, so we pick once based on the initial
    // element count. Threshold is generous (500 nodes) because `pixelRatio: 1`
    // visibly softens text on HiDPI displays.
    const initialNodeCount = (options.elements || []).filter((el) => el.group !== 'edges').length;
    const pixelRatio = initialNodeCount > 500 ? 1 : 'auto';

    this.cy = cytoscape({
      container,
      style: options.stylesheet || [],
      elements: options.elements || [],
      layout: { name: 'preset' },
      minZoom: MIN_FIT_ZOOM,
      maxZoom: 5,
      wheelSensitivity: 0.3,
      hideEdgesOnViewport: true,
      textureOnViewport: true,
      pixelRatio,
      ...options.cyOptions,
    });

    // Defensive runtime shim: some Cytoscape extensions call `cy.undoRedo()`
    // when handling taps/cues. This app does not include cytoscape-undo-redo,
    // so provide a minimal no-op implementation to avoid hard crashes.
    if (typeof this.cy.undoRedo !== 'function') {
      this.cy.undoRedo = () => ({
        do: () => {},
        undo: () => {},
        redo: () => {},
      });
    }

    // Initialize the expand/collapse extension if available. Wrap in a
    // defensive try/catch because some versions of the extension (or
    // incompatible builds) can throw during init and crash the whole
    // component mount — fail gracefully instead of letting React error
    // boundaries trigger. See runtime console errors referencing
    // "n.action is not a function" from the plugin.
    try {
      if (this.cy && typeof this.cy.expandCollapse === 'function') {
        this.ecApi = this.cy.expandCollapse({
          fisheye: false,
          animate: true,
          animationDuration: 500,
          cueEnabled: true,
          // We do not register cytoscape-undo-redo in this app. Keep expand/
          // collapse operations independent so cue taps never call cy.undoRedo().
          undoable: false,
        });
      } else {
        console.warn('[GraphAdapter] cytoscape-expand-collapse not available; expand/collapse disabled.');
        this.ecApi = null;
      }
    } catch (err) {
      console.warn('[GraphAdapter] cytoscape-expand-collapse init failed — disabling expand/collapse:', err);
      this.ecApi = null;
    }

    return this.cy;
  }

  destroy() {
    if (this.layoutWorker) {
      this.layoutWorker.terminate();
      this.layoutWorker = null;
    }
    this._workerState.clear();
    if (this.cy) {
      this.cy.destroy();
      this.cy = null;
      this.ecApi = null;
    }
  }

  // ── Topology-signature layout cache (S13) ─────────────────────────

  /**
   * Cheap stable hash of the current topology: sorted node ids (with parent)
   * plus sorted edge endpoints. Same node-set + same parent assignments +
   * same edges → same signature.
   */
  _topologySignature() {
    if (!this.cy) return '';
    const ids = [];
    this.cy.nodes().forEach((n) => {
      ids.push(`n:${n.id()}@${n.data('parent') ?? ''}`);
    });
    this.cy.edges().forEach((e) => {
      ids.push(`e:${e.id()}>${e.data('source')}-${e.data('target')}`);
    });
    ids.sort();
    return ids.join('|');
  }

  /**
   * If the current topology matches a previously laid-out one, restore the
   * cached positions and return true. Otherwise return false.
   */
  _restoreCachedPositionsIfMatch() {
    const sig = this._topologySignature();
    if (sig === this._lastSignature) {
      // Identical to last laid-out topology — positions are already correct.
      perfCount('cy.layout.cache.noop');
      return true;
    }
    const cached = this._positionCache.get(sig);
    if (!cached) {
      this._lastSignature = sig;
      return false;
    }
    this.cy.startBatch();
    this.cy.nodes().forEach((n) => {
      const p = cached[n.id()];
      if (p) n.position(p);
    });
    this.cy.endBatch();
    // Positions are restored from a different prior topology (e.g. user
    // toggled filter A off after having filtered to a small subgraph). The
    // viewport was last fitted to the other topology, so it will be at the
    // wrong zoom/pan for these positions. Re-fit to the restored spread,
    // preferring connected nodes when the graph is mostly isolated.
    const connectedAfterRestore = this.cy.nodes().filter(n => n.degree() > 0);
    const totalAfterRestore = this.cy.nodes().length;
    if (connectedAfterRestore.length > 0 && connectedAfterRestore.length <= totalAfterRestore * 0.25) {
      this.cy.fit(connectedAfterRestore, 80);
    } else {
      this.cy.fit(undefined, 30);
    }
    if (this.cy.zoom && this.cy.zoom() < MIN_FIT_ZOOM) this.cy.zoom(MIN_FIT_ZOOM);
    // Refresh LRU recency by re-inserting.
    this._positionCache.delete(sig);
    this._positionCache.set(sig, cached);
    this._lastSignature = sig;
    perfCount('cy.layout.cache.hit');
    return true;
  }

  /**
   * Snapshot current positions under the current topology signature, with
   * LRU eviction.
   */
  _cacheCurrentPositions() {
    if (!this.cy) return;
    const sig = this._topologySignature();
    const map = {};
    this.cy.nodes().forEach((n) => {
      const p = n.position();
      map[n.id()] = { x: p.x, y: p.y };
    });
    if (this._positionCache.has(sig)) {
      this._positionCache.delete(sig);
    }
    this._positionCache.set(sig, map);
    while (this._positionCache.size > POSITION_CACHE_LIMIT) {
      const oldestKey = this._positionCache.keys().next().value;
      this._positionCache.delete(oldestKey);
    }
    this._lastSignature = sig;
  }

  // ── Web Worker ────────────────────────────────────────────────────

  /**
   * Lazily initialise the layout Web Worker.
   * Returns the worker instance, or null if workers are unsupported / failed.
   */
  _initWorker() {
    if (this._workerFailed) return null;
    if (this.layoutWorker) return this.layoutWorker;

    try {
      this.layoutWorker = new Worker(
        /* webpackChunkName: "layout-worker" */
        new URL('./layout-worker.js', import.meta.url)
      );
      // If the worker throws an unrecoverable error, fall back permanently
      this.layoutWorker.onerror = (err) => {
        console.warn('[GraphAdapter] Layout worker error, falling back to main thread:', err);
        this._workerFailed = true;
        this.layoutWorker = null;
      };
      return this.layoutWorker;
    } catch (e) {
      console.warn('[GraphAdapter] Failed to create layout worker, using main thread:', e);
      this._workerFailed = true;
      return null;
    }
  }

  /**
   * Serialize the current Cytoscape elements into a plain array suitable
   * for structured-clone transfer to the worker.
   */
  _serializeElements() {
    perfMark('cy:worker:serialize:start');
    const elements = [];
    this.cy.elements().forEach((el) => {
      if (el.isNode()) {
        const entry = { data: this._serializeElementDataForWorker(el), group: el.group() };
        const pos = el.position();
        entry.position = { x: pos.x, y: pos.y };
        elements.push(entry);
      } else {
        elements.push({ data: this._serializeElementDataForWorker(el), group: el.group() });
      }
    });
    perfMeasure('cy:worker:serialize', 'cy:worker:serialize:start', undefined, { count: elements.length });
    return elements;
  }

  _serializeElementDataForWorker(el) {
    const data = sanitizeWorkerDataForLayout(el.data());
    data.id = el.id();

    if (el.isEdge()) {
      data.source = el.source().id();
      data.target = el.target().id();
      delete data.parent;
    } else if (el.isNode()) {
      const parent = el.parent();
      if (parent.length > 0) {
        data.parent = parent.id();
      } else {
        delete data.parent;
      }
      delete data.source;
      delete data.target;
    }

    return data;
  }

  /**
   * Phase 12 — Compute the delta vs `_workerState`. Returns
   * `{ added, updated, removedIds, totalSent }` where each entry contains
   * data + (for nodes) position. We compare `data` by shallow-key-value
   * equality and position by exact x/y match, since this is what the worker
   * needs to mirror.
   */
  _serializeDelta() {
    perfMark('cy:worker:serialize:start');
    const added = [];
    const updated = [];
    const seenIds = new Set();
    let totalSent = 0;

    this.cy.elements().forEach((el) => {
      const id = el.id();
      seenIds.add(id);
      const isNode = el.isNode();
      const data = this._serializeElementDataForWorker(el);
      const group = el.group();
      const pos = isNode ? el.position() : null;

      const prev = this._workerState.get(id);
      if (!prev) {
        const entry = { data: { ...data }, group };
        if (isNode) entry.position = { x: pos.x, y: pos.y };
        added.push(entry);
        totalSent++;
        this._workerState.set(id, {
          data: { ...data },
          x: isNode ? pos.x : 0,
          y: isNode ? pos.y : 0,
          group,
        });
        return;
      }

      // Shallow-compare data: if any value differs, send an update.
      let dataChanged = false;
      for (const key in data) {
        if (data[key] !== prev.data[key]) { dataChanged = true; break; }
      }
      if (!dataChanged) {
        for (const key in prev.data) {
          if (!(key in data)) { dataChanged = true; break; }
        }
      }
      const positionChanged = isNode && (prev.x !== pos.x || prev.y !== pos.y);

      if (dataChanged || positionChanged) {
        const entry = { data: { ...data }, group };
        if (isNode) entry.position = { x: pos.x, y: pos.y };
        updated.push(entry);
        totalSent++;
        prev.data = { ...data };
        if (isNode) { prev.x = pos.x; prev.y = pos.y; }
      }
    });

    const removedIds = [];
    this._workerState.forEach((_, id) => {
      if (!seenIds.has(id)) {
        removedIds.push(id);
        this._workerState.delete(id);
      }
    });

    perfMeasure('cy:worker:serialize', 'cy:worker:serialize:start', undefined, {
      added: added.length, updated: updated.length, removed: removedIds.length, totalSent,
    });
    return { added, updated, removedIds, totalSent };
  }

  /**
   * Apply worker-computed positions back to the live Cytoscape graph.
   * Optionally animates the transition.
   *
   * @param {Object} positions - Map of node id -> {x, y}
   * @param {boolean} animate - Whether to animate the move
   * @param {number} duration - Animation duration in ms
   * @returns {Promise} Resolves when positions are applied (and animation finishes)
   */
  _applyWorkerPositions(positions, animate = false, duration = 400, meta = {}) {
    if (!this.cy) return Promise.resolve();
    perfMark('cy:worker:applyPositions:start');

    // After we apply positions to the live cy, the worker also has those
    // positions (it just computed them). Sync `_workerState` so the next
    // `_serializeDelta` doesn't redundantly resend unchanged positions.
    if (positions) {
      for (const id in positions) {
        const prev = this._workerState.get(id);
        if (prev) {
          prev.x = positions[id].x;
          prev.y = positions[id].y;
        }
      }
    }

    const applyFit = () => {
      perfCount('cy.fit');
      perfMark('cy:fit');
      // If there's exactly one visible node, center and zoom to a readable
      // level rather than letting `fit()` pick a tiny zoom.
      const visibleNodeCount = this.cy.nodes().length;
      if (visibleNodeCount === 1) {
        try { this.cy.center(this.cy.nodes()); } catch (e) { /* ignore */ }
        const maxZ = typeof this.cy.maxZoom === 'function' ? this.cy.maxZoom() : 5;
        const targetZ = Math.min(maxZ, SINGLE_NODE_ZOOM);
        if (this.cy.zoom() !== targetZ) this.cy.zoom(targetZ);
        return;
      }
      // Phase 9 — Sparse styling is now driven by data buckets and a stylesheet
      // rule, not per-element inline styles. We compute the bucket once and
      // batch a single `data()` write per node.
      if (meta._sparseGraph) {
        const maxDegree = this.cy.nodes().max((n) => n.degree()).value || 1;
        this.cy.startBatch();
        this.cy.nodes().forEach((n) => {
          const deg = n.degree();
          const ratio = Math.log2(deg + 1) / Math.log2(maxDegree + 1);
          const bucket = ratio > 0.66 ? 'large' : ratio > 0.33 ? 'medium' : 'small';
          if (n.data('_sparseBucket') !== bucket) n.data('_sparseBucket', bucket);
          if (!n.data('_sparseShortLabel')) {
            const rawLabel = (n.data('label') || '').toString();
            const parts = rawLabel.split(/\s+/).filter(Boolean);
            const compact = parts.length >= 2
              ? `${parts[0][0] || ''}${parts[1][0] || ''}`
              : (rawLabel.length > 4 ? rawLabel.slice(0, 4) : rawLabel);
            n.data('_sparseShortLabel', compact);
          }
          if (!n.data('_sparseGraph')) n.data('_sparseGraph', true);
        });
        this.cy.edges().forEach((e) => {
          if (!e.data('_sparseGraph')) e.data('_sparseGraph', true);
        });
        this.cy.endBatch();
        this.cy.fit(undefined, 45);
        // Clamp extremely small zooms after fit to keep nodes readable.
        if (this.cy.zoom && this.cy.zoom() < MIN_FIT_ZOOM) this.cy.zoom(MIN_FIT_ZOOM);
        return;
      }
      // Strip sparse data flags so the stylesheet rules deactivate. One
      // batched data() per element; no inline-style writes.
      const hasAnySparse = this.cy.nodes().filter('[?_sparseGraph]').length > 0;
      if (hasAnySparse) {
        this.cy.startBatch();
        this.cy.nodes().forEach((n) => {
          if (n.data('_sparseGraph')) n.removeData('_sparseGraph');
          if (n.data('_sparseBucket')) n.removeData('_sparseBucket');
          if (n.data('_sparseShortLabel')) n.removeData('_sparseShortLabel');
        });
        this.cy.edges().forEach((e) => {
          if (e.data('_sparseGraph')) e.removeData('_sparseGraph');
        });
        this.cy.endBatch();
      }
      // S6 / Phase 11 — Skip fit on incremental layouts. The first layout
      // and any caller that explicitly asks (`meta.fitOnComplete`) still
      // fits.
      if (meta.fitOnComplete !== false && !meta.isIncremental) {
        // When the graph is mostly isolated singletons (common after a
        // tight edge filter like "Immediate Family"), fitting all nodes
        // buries the meaningful connected subgraph in a sea of grey dots.
        // Instead, fit to just the connected nodes so the user sees the
        // actual relationships front and center at a readable zoom.
        const connectedNodes = this.cy.nodes().filter(n => n.degree() > 0);
        const totalNodes = this.cy.nodes().length;
        const useConnectedFit =
          connectedNodes.length > 0 &&
          connectedNodes.length <= totalNodes * 0.25; // ≥75% isolated
        if (useConnectedFit) {
          this.cy.fit(connectedNodes, 80);
        } else {
          this.cy.fit(undefined, 30);
        }
        if (this.cy.zoom && this.cy.zoom() < MIN_FIT_ZOOM) this.cy.zoom(MIN_FIT_ZOOM);
      }
    };

    if (!animate) {
      this.cy.startBatch();
      this.cy.nodes().forEach((node) => {
        const pos = positions[node.id()];
        if (pos) node.position(pos);
      });
      this.cy.endBatch();
      applyFit();
      perfMeasure('cy:worker:applyPositions', 'cy:worker:applyPositions:start');
      return Promise.resolve();
    }

    // Animated: move each node to its new position
    return new Promise((resolve) => {
      const nodes = this.cy.nodes();
      let remaining = 0;
      nodes.forEach((node) => {
        const pos = positions[node.id()];
        if (pos) {
          remaining++;
          node.animate(
            { position: pos },
            {
              duration,
              easing: 'ease-in-out-quad',
              complete: () => {
                remaining--;
                if (remaining === 0) {
                  applyFit();
                  perfMeasure('cy:worker:applyPositions', 'cy:worker:applyPositions:start');
                  resolve();
                }
              },
            }
          );
        }
      });
      if (remaining === 0) {
        applyFit();
        perfMeasure('cy:worker:applyPositions', 'cy:worker:applyPositions:start');
        resolve();
      }
    });
  }

  // ── Data (DIFFING) ────────────────────────────────────────────────

  /**
   * Diff-based element update. Compares incoming elements vs current graph.
   * Only adds/removes/updates changed elements — avoids full rebuild.
   *
   * @param {Array} elements - New Cytoscape elements array
   * @returns {{ topologyChanged: boolean }} Whether node/edge IDs changed
   */
  /**
   * Diff-based element update.
   *
   * Two modes:
   *
   *   1. Legacy (array of elements): walks every cy element to compute the
   *      diff. Kept for callers that don't yet have delta information.
   *   2. Dirty-id (Phase 7): caller supplies
   *        { elements, addedIds, changedIds, removedIds }
   *      We touch only those ids, plus a dev-mode assertion comparing the
   *      caller's diff against a recomputed full diff.
   */
  setElements(elementsOrDelta) {
    if (!this.cy) return { topologyChanged: true };
    perfMark('cy:setElements:start');

    const isDelta = elementsOrDelta != null
      && !Array.isArray(elementsOrDelta)
      && Array.isArray(elementsOrDelta.elements);

    const elements = isDelta ? elementsOrDelta.elements : elementsOrDelta;

    const incomingById = new Map();
    for (const el of elements) {
      incomingById.set(el.data.id, el);
    }

    let topologyChanged = false;
    let addedCount = 0;
    let removedCount = 0;
    let updatedCount = 0;

    // Snapshot pre-state for the dev-mode assertion below. Only collected in
    // development and only when running the dirty-ID path.
    const _devLiveIdsBefore =
      isDelta && process.env.NODE_ENV === 'development'
        ? new Set(this.cy.elements().map((el) => el.id()))
        : null;

    this.cy.startBatch();
    try {
      if (isDelta) {
        const { addedIds = [], changedIds = [], removedIds = [] } = elementsOrDelta;

        // Pre-un-parent children of any compound parents that are about to
        // be removed. Cytoscape's `cy.remove()` on a compound parent
        // cascade-removes its children — which would silently delete child
        // nodes that the caller intends to keep (e.g. cluster-mode toggle
        // OFF: combo parent removed, but its 8 children are in `changedIds`
        // with `parent: null`). Move them to no-parent first so the cascade
        // has nothing to take.
        if (removedIds.length > 0) {
          const removedSet = new Set(removedIds);
          for (let i = 0; i < removedIds.length; i++) {
            const cyEl = this.cy.$id(removedIds[i]);
            if (cyEl.length > 0 && cyEl.isNode() && cyEl.isParent()) {
              cyEl.children().forEach((child) => {
                if (!removedSet.has(child.id())) {
                  child.move({ parent: null });
                }
              });
            }
          }
        }

        // Remove
        if (removedIds.length > 0) {
          const toRemove = this.cy.collection();
          for (let i = 0; i < removedIds.length; i++) {
            const cyEl = this.cy.$id(removedIds[i]);
            if (cyEl.length > 0) toRemove.merge(cyEl);
          }
          if (toRemove.length > 0) {
            removedCount = toRemove.length;
            toRemove.remove();
            topologyChanged = true;
          }
        }

        // Add BEFORE Update. Compound parent re-parenting in
        // `_updateElement` calls `cyEl.move({ parent: comboId })`, which
        // silently fails if `comboId` is in the SAME delta's `addedIds`
        // and hasn't been added yet. Adding first guarantees new combo
        // parents (and any other newly-added nodes referenced as the
        // target of a parent change) exist before the move runs.
        const toAddNodes = [];
        const toAddEdges = [];
        for (let i = 0; i < addedIds.length; i++) {
          const id = addedIds[i];
          const incoming = incomingById.get(id);
          if (!incoming) continue;
          if (this.cy.$id(id).length === 0) {
            if (incoming.group === 'edges') {
              toAddEdges.push(incoming);
            } else {
              toAddNodes.push(incoming);
            }
          }
        }

        if (toAddNodes.length > 0) {
          this.cy.add(toAddNodes);
        }

        let addedEdgeCount = 0;
        if (toAddEdges.length > 0) {
          const validEdges = [];
          let droppedEdges = 0;
          for (let i = 0; i < toAddEdges.length; i++) {
            const edge = toAddEdges[i];
            const src = edge.data?.source;
            const dst = edge.data?.target;
            if (src && dst && this.cy.$id(src).length > 0 && this.cy.$id(dst).length > 0) {
              validEdges.push(edge);
            } else {
              droppedEdges++;
            }
          }
          if (validEdges.length > 0) {
            this.cy.add(validEdges);
            addedEdgeCount = validEdges.length;
          }
          if (droppedEdges > 0) {
            // eslint-disable-next-line no-console
            console.warn('[GraphAdapter] Dropped edge(s) with missing endpoint(s) during delta add.', {
              droppedEdges,
            });
          }
        }

        addedCount = toAddNodes.length + addedEdgeCount;
        if (addedCount > 0) {
          topologyChanged = true;
        }

        // Update — only walk changedIds
        for (let i = 0; i < changedIds.length; i++) {
          const id = changedIds[i];
          const incoming = incomingById.get(id);
          if (!incoming) continue;
          const cyEl = this.cy.$id(id);
          if (cyEl.length === 0) {
            // Caller said "changed" but we don't have it — fall through to add.
            continue;
          }
          const prev = this._prevIncoming.get(id);
          if (incoming !== prev) {
            this._updateElement(cyEl, incoming);
            updatedCount++;
          } else {
            perfCount('cy.setElements.skipped');
          }
        }

        // Dev-mode assertion: compare caller's delta against a recomputed full
        // diff. Loud in development, silent in production.
        if (process.env.NODE_ENV === 'development') {
          this._verifyFullDiff(elements, incomingById, _devLiveIdsBefore, {
            addedIds, changedIds, removedIds,
          });
        }
      } else {
        // Legacy full-scan path.
        const currentIds = new Set();
        const toRemove = this.cy.collection();
        this.cy.elements().forEach((cyEl) => {
          const id = cyEl.id();
          currentIds.add(id);
          const incoming = incomingById.get(id);
          if (!incoming) {
            toRemove.merge(cyEl);
            topologyChanged = true;
          } else {
            const prev = this._prevIncoming.get(id);
            if (incoming !== prev) {
              this._updateElement(cyEl, incoming);
              updatedCount++;
            } else {
              perfCount('cy.setElements.skipped');
            }
          }
        });
        if (toRemove.length > 0) {
          removedCount = toRemove.length;
          toRemove.remove();
        }

        const toAddNodes = [];
        const toAddEdges = [];
        for (const el of elements) {
          if (!currentIds.has(el.data.id)) {
            if (el.group === 'edges') {
              toAddEdges.push(el);
            } else {
              toAddNodes.push(el);
            }
            topologyChanged = true;
          }
        }

        if (toAddNodes.length > 0) {
          this.cy.add(toAddNodes);
        }

        let addedEdgeCount = 0;
        if (toAddEdges.length > 0) {
          const validEdges = [];
          let droppedEdges = 0;
          for (let i = 0; i < toAddEdges.length; i++) {
            const edge = toAddEdges[i];
            const src = edge.data?.source;
            const dst = edge.data?.target;
            if (src && dst && this.cy.$id(src).length > 0 && this.cy.$id(dst).length > 0) {
              validEdges.push(edge);
            } else {
              droppedEdges++;
            }
          }
          if (validEdges.length > 0) {
            this.cy.add(validEdges);
            addedEdgeCount = validEdges.length;
          }
          if (droppedEdges > 0) {
            // eslint-disable-next-line no-console
            console.warn('[GraphAdapter] Dropped edge(s) with missing endpoint(s) during full add.', {
              droppedEdges,
            });
          }
        }

        addedCount = toAddNodes.length + addedEdgeCount;
        if (addedCount > 0) {
          topologyChanged = true;
        }
      }
    } finally {
      this.cy.endBatch();
    }

    // Cache incoming references for next diff cycle
    this._prevIncoming = incomingById;

    perfMeasure('cy:setElements', 'cy:setElements:start', undefined, {
      addedCount, removedCount, updatedCount,
    });
    return { topologyChanged, addedCount, removedCount, updatedCount };
  }

  /**
   * Dev-only sanity check: did the caller-supplied delta cover everything a
   * full-scan diff would have touched?
   *
   * Compares pre-mutation cy state (`liveIdsBefore`) against the new
   * `incomingById`, plus the per-element identity check (`prev !== el`)
   * that signals "needs an update". Anything in either of those sets that
   * is NOT in the supplied delta means the caller missed it. We log
   * (don't throw) so the page keeps working while the bug is investigated.
   */
  _verifyFullDiff(elements, incomingById, liveIdsBefore, supplied) {
    if (!liveIdsBefore) return;

    const suppliedSet = new Set([
      ...supplied.addedIds,
      ...supplied.changedIds,
      ...supplied.removedIds,
    ]);

    // Removals: ids that existed before but aren't in incoming.
    const missedRemovals = [];
    liveIdsBefore.forEach((id) => {
      if (!incomingById.has(id) && !suppliedSet.has(id)) missedRemovals.push(id);
    });

    // Adds + changes: any incoming element whose ref differs from prev.
    const missedAddedOrChanged = [];
    for (const el of elements) {
      const id = el.data.id;
      const prev = this._prevIncoming.get(id);
      if (prev !== el && !suppliedSet.has(id)) missedAddedOrChanged.push(id);
    }

    if (missedRemovals.length > 0 || missedAddedOrChanged.length > 0) {
      // eslint-disable-next-line no-console
      console.warn('[GraphAdapter] setElements delta mismatch — caller missed ids the full-scan diff would have touched.', {
        missedRemovals: missedRemovals.slice(0, 5),
        missedAddedOrChanged: missedAddedOrChanged.slice(0, 5),
        missedRemovalsCount: missedRemovals.length,
        missedAddedOrChangedCount: missedAddedOrChanged.length,
      });
    }
  }

  /**
   * Update an existing Cytoscape element's data and style to match incoming.
   *
   * Phase 7 — data and style writes are split. The expensive `cy.style(...)`
   * call only fires when the inline style object's identity actually changed
   * (or when explicitly forced). Data-only changes never re-apply style.
   */
  _updateElement(cyEl, incoming) {
    const isNode = cyEl.isNode();
    // Build a diff object and apply all changed data properties in one call
    const currentData = cyEl.data();
    const newData = incoming.data;
    let diffObj = null;
    const incomingParent = newData.parent ?? null;
    const currentParent = currentData.parent ?? null;
    let parentChanged = false;

    // Compound parent changes must use move(), not data(), otherwise
    // existing nodes won't actually re-parent during diff updates.
    if (isNode && incomingParent !== currentParent) {
      parentChanged = true;
      cyEl.move({ parent: incomingParent });
    }

    for (const key in newData) {
      if (key === 'id') continue;
      if (key === 'parent') continue;
      if (currentData[key] !== newData[key]) {
        if (!diffObj) diffObj = {};
        diffObj[key] = newData[key];
      }
    }

    // Keep data(parent) in sync for downstream reads after move().
    if (parentChanged) {
      if (!diffObj) diffObj = {};
      diffObj.parent = incomingParent;
    }

    if (diffObj) {
      cyEl.data(diffObj);
      perfCount('cy.update.data');
    }

    // Style is a separate write path. We compare *reference identity* of the
    // incoming style object to the previously-applied one — the transform's
    // cache returns the same style object when nothing changed, so this skips
    // re-applying inline style for pure data diffs.
    //
    // F-20 — `cyEl.style(newStyle)` MERGES (only sets keys present in
    // newStyle), so any property that was previously bypassed but isn't in
    // the new style would otherwise persist. Track the previous bypass keys
    // and call `cyEl.removeStyle(...)` on the dropped subset before applying
    // the new style. Covers transitions like:
    //   suggested → confirmed   (`border-style: dashed` cleared)
    //   custom border-color → match bgColor (inline border-color cleared)
    //   opacity 0.7 → undefined (inline opacity cleared)
    //   stale `background-image: 'true'` from pre-F-19 builds (cleared)
    if (!this._lastStyleByEl) this._lastStyleByEl = new WeakMap();
    const prevStyle = this._lastStyleByEl.get(cyEl);
    const newStyle = incoming.style;

    if (prevStyle === newStyle) {
      // Same identity — fast no-op.
      if (newStyle) perfCount('cy.update.style.skipped');
      return;
    }

    const prevKeys = prevStyle ? Object.keys(prevStyle) : null;
    const newKeys = newStyle ? Object.keys(newStyle) : null;

    // Drop any inline bypass that was previously set but is no longer in the
    // incoming style.
    if (prevKeys && prevKeys.length > 0) {
      let dropped = '';
      for (const k of prevKeys) {
        if (!newKeys || !(k in newStyle)) {
          dropped += (dropped ? ' ' : '') + k;
        }
      }
      if (dropped) {
        cyEl.removeStyle(dropped);
        perfCount('cy.update.style.cleared');
      }
    }

    if (newStyle && newKeys.length > 0) {
      cyEl.style(newStyle);
      this._lastStyleByEl.set(cyEl, newStyle);
      perfCount('cy.update.style');
    } else {
      // No incoming style — drop the cached entry so future updates start
      // fresh. (Removing from the WeakMap also lets `cyEl` be GC'd cleanly
      // when the element is later removed from cy.)
      this._lastStyleByEl.delete(cyEl);
    }
  }

  /**
   * Position newly-added nodes near their connected neighbors.
   * Gives the layout a warm start so very few iterations are needed.
   */
  _positionNewNodesNearNeighbors() {
    if (!this.cy) return;
    this.cy.nodes().forEach(node => {
      const pos = node.position();
      if (pos.x !== 0 || pos.y !== 0) return; // already positioned
      const neighbors = node.neighborhood().nodes();
      let sumX = 0, sumY = 0, count = 0;
      neighbors.forEach(n => {
        const nPos = n.position();
        if (nPos.x !== 0 || nPos.y !== 0) { sumX += nPos.x; sumY += nPos.y; count++; }
      });
      if (count > 0) {
        node.position({
          x: sumX / count + (Math.random() - 0.5) * 80,
          y: sumY / count + (Math.random() - 0.5) * 80,
        });
      }
    });
  }

  /**
   * Run layout with graph-size guards and smart animation.
   *
   * For supported layouts, computation is offloaded to a Web Worker so the
   * main thread stays responsive. Cola and any fallback scenarios still run
   * on the main thread.
   *
   * @param {Object} options - Cytoscape layout options override
   * @param {{ addedCount?: number, targetNodeId?: string }} meta - Topology change metadata from setElements
   * @param {Function} onComplete - Called when layout finishes
   */
  runLayout(options = {}, meta = {}, onComplete) {
    if (!this.cy) { onComplete?.(); return; }
    perfCount('cy.layout.run');
    perfMark('cy:layout:run:start');
    const _userOnComplete = onComplete;
    const _instrumentedComplete = (...args) => {
      // Snapshot positions for the current topology so future runs with the
      // same id set can short-circuit (S13).
      try { this._cacheCurrentPositions(); } catch (e) { /* ignore */ }
      perfMeasure('cy:layout:run', 'cy:layout:run:start');
      _userOnComplete?.(...args);
    };

    // Cancel any running layout before starting a new one
    if (this.currentLayout) {
      this.currentLayout.stop();
      this.currentLayout = null;
    }

    // S13 — Topology-signature cache short-circuit. Skip when the caller
    // explicitly requested a fresh layout (`meta.userExplicitLayout`) or a
    // randomized layout, since those should re-run regardless of cache.
    const isExplicitUserLayout = !!meta.userExplicitLayout;
    const wantsFreshRandomized = options.randomize === true;
    if (this.initialLayoutDone && !isExplicitUserLayout && !wantsFreshRandomized) {
      if (this._restoreCachedPositionsIfMatch()) {
        perfMeasure('cy:layout:run', 'cy:layout:run:start');
        _userOnComplete?.();
        return null;
      }
    }

    const nodeCount = this.cy.nodes().length;
    const addedCount = meta.addedCount || 0;
    const targetNodeId = meta.targetNodeId || null;
    const isLargeChange = addedCount > 50;
    const isIncremental = this.initialLayoutDone && !options.randomize;
    let defaults = options.name ? {} : { ...LAYOUT_PRESETS.fcose.options };

    // Pre-position new nodes near neighbors for a warm start
    if (isIncremental && addedCount > 0) {
      this._positionNewNodesNearNeighbors();
    }

    // targetRadial uses concentric (O(n log n)) — skip force-directed guards entirely
    const isTargetRadial = options.name === 'concentric' && targetNodeId;

    // For large topology changes or big graphs, use native cose (non-blocking, much faster)
    if (!isTargetRadial && (!options.name || options.name === 'fcose')) {
      if (nodeCount > 500 || isLargeChange) {
        defaults = {
          name: 'cose',
          animate: isIncremental && !isLargeChange,
          animationDuration: isIncremental ? 300 : 400,
          nodeRepulsion: 5000,
          idealEdgeLength: 100,
          gravity: 0.5,
          numIter: isIncremental ? (nodeCount > 1000 ? 20 : 30) : (nodeCount > 1000 ? 30 : 50),
          nodeOverlap: 20,
        };
      } else if (isIncremental) {
        // Warm start: nodes pre-positioned near neighbors, very few iterations needed
        defaults.numIter = nodeCount > 200 ? 300 : 500;
        defaults.animationDuration = 300;
      } else if (nodeCount > 200) {
        defaults.numIter = 2500;
      }
    }

    // Auto-switch to concentric when edge density is very low (mostly disconnected nodes).
    // Mimics ReGraph's automatic spiral layout for sparse search results.
    // Only override when the user hasn't explicitly chosen a layout via the switcher.
    const edgeCount = this.cy.edges().length;
    const edgeDensity = nodeCount > 0 ? edgeCount / nodeCount : 1;
    const userExplicit = meta.userExplicitLayout || false;
    // The sparse spiral places nodes on concentric rings spanning a fixed value
    // range (~0–560) grouped by `levelWidth` (~5), i.e. roughly 100+ rings. It
    // only reads as a dense spiral when there are many nodes per ring (large
    // graphs). After edge-type filters (e.g. "Family → Immediate Family") the
    // visible graph commonly collapses to hundreds of isolated "leftover"
    // context nodes plus a handful of actual matching edges (verified at
    // runtime: 427 nodes, 1 edge after that filter). With few rings populated
    // the layout radius explodes and cy.fit() zooms out to unreadable dots.
    // Below this floor, fall through to fcose, which clusters connected pairs
    // tightly and tiles isolated nodes in a padded grid at a readable zoom.
    // Threshold raised to 1000 so typical filtered graphs always use fcose.
    const SPARSE_SPIRAL_MIN_NODES = 1000;
    let densityOverride = false;
    if (!isTargetRadial && !userExplicit && edgeDensity < 0.3 && nodeCount >= SPARSE_SPIRAL_MIN_NODES) {
      // Concentric with degree-based ordering: connected nodes center, orphans spread on outer rings.
      // Spacing adapts to node count so rings don't overlap at scale.
      const spacing = nodeCount > 1000 ? 5 : nodeCount > 500 ? 8 : 14;
      defaults = {
        name: 'concentric',
        animate: true,
        animationDuration: 500,
        minNodeSpacing: spacing,
        equidistant: false,
        // concentric + levelWidth are function-valued — re-created in the worker
      };
      densityOverride = true;
      meta._sparseGraph = true; // signal to worker for type-aware ring assignment
      debugLog('[GraphAdapter] Low edge density (%.2f) — auto-switching to concentric layout', edgeDensity);
    }

    // After the first layout, don't randomize — keeps existing positions stable
    if (isIncremental) {
      defaults.randomize = false;
    }

    // When density override is active, ignore the caller's options (which would re-apply fcose)
    const mergedOptions = densityOverride ? { ...defaults } : { ...defaults, ...options };
    const layoutName = mergedOptions.name || 'fcose';

    // S7 — disable layout animation for non-fcose layouts above 250 nodes.
    // fcose/cose already gate animation via the size-based path above.
    const NON_FCOSE_ANIMATED = new Set(['cola', 'dagre', 'concentric', 'circle', 'breadthfirst', 'grid']);
    if (NON_FCOSE_ANIMATED.has(layoutName) && nodeCount > 250 && mergedOptions.animate) {
      mergedOptions.animate = false;
      mergedOptions.animationDuration = 0;
    }

    // F-21 — Component packing (fcose default `packComponents: true`) tile-
    // packs disconnected components into a near-zero-padding grid, which
    // produces a hard-to-read stack when a filter collapses the visible graph
    // to many isolated nodes. Increase tiling padding adaptively so every
    // filtered graph size gets visible breathing room between components.
    // Verified case: 427 nodes, 1 edge (Immediate Family filter) — without
    // padding the 426 isolated singletons pack into a near-invisible grid.
    if (layoutName === 'fcose') {
      // Adaptive tiling padding: more breathing room for smaller node counts
      // where each component box is individually discernible.
      const tilingPadding =
        nodeCount <= 50  ? 80 :
        nodeCount <= 150 ? 65 :
        nodeCount <= 300 ? 45 :
        nodeCount <= 500 ? 30 : null; // null = keep fcose default for large graphs
      if (mergedOptions.packComponents == null) {
        mergedOptions.packComponents = true;
      }
      if (tilingPadding != null) {
        if (mergedOptions.tilingPaddingHorizontal == null) {
          mergedOptions.tilingPaddingHorizontal = tilingPadding;
        }
        if (mergedOptions.tilingPaddingVertical == null) {
          mergedOptions.tilingPaddingVertical = tilingPadding;
        }
      }
    }

    debugLog('[GraphAdapter] runLayout:', layoutName, 'nodeCount:', nodeCount, 'targetNodeId:', targetNodeId, 'isTargetRadial:', isTargetRadial, 'workerFailed:', this._workerFailed);

    // ── Attempt Web Worker offload ─────────────────────────────────
    if (WORKER_SUPPORTED_LAYOUTS.has(layoutName)) {
      const worker = this._initWorker();
      if (worker) {
        return this._runLayoutInWorker(worker, mergedOptions, isIncremental, _instrumentedComplete, meta);
      }
      // Worker unavailable — fall through to main-thread layout
    } else if (layoutName === 'cola') {
      debugLog('[GraphAdapter] Cola layout runs on the main thread (requires DOM context).');
    }

    // ── Main-thread fallback ───────────────────────────────────────
    // For concentric with a target node, inject BFS-based concentric function
    if (layoutName === 'concentric' && targetNodeId && this.cy) {
      this._injectConcentricBFS(mergedOptions, targetNodeId);
    } else if (layoutName === 'concentric' && densityOverride && this.cy) {
      // Sparse-graph concentric: type-aware ring assignment on main thread
      const NODE_TYPE_TIER = { person: 4, org: 3, fund: 2, education: 2, engagement: 1 };
      mergedOptions.concentric = (node) => {
        const deg = node.degree();
        if (deg <= 1) {
          const id = node.id();
          let hash = 0;
          for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) % 9973;
          const bucket = hash % 48;
          const bandBase = deg === 0 ? 0 : 80;
          return bandBase + bucket * 6;
        }
        const nt = (node.data('vertexType') || node.data('type') || '').toLowerCase();
        const tier = NODE_TYPE_TIER[nt] || 0;
        return 520 + deg * 18 + tier * 3;
      };
      mergedOptions.levelWidth = () => Math.max(5, Math.ceil(this.cy.nodes().length / 450));
    }
    return this._runLayoutOnMainThread(mergedOptions, _instrumentedComplete);
  }

  /**
   * Inject BFS-based concentric/levelWidth functions for target-radial on the main thread.
   */
  _injectConcentricBFS(layoutOptions, targetNodeId) {
    const targetNode = this.cy.$id(targetNodeId);
    if (targetNode.length === 0) {
      console.warn('[GraphAdapter] Target node not found for BFS:', targetNodeId);
      layoutOptions.concentric = (node) => node.degree();
      layoutOptions.levelWidth = () => 2;
      return;
    }
    const distances = {};
    const visited = new Set();
    const queue = [targetNodeId];
    visited.add(targetNodeId);
    distances[targetNodeId] = 0;
    let maxDist = 0;
    while (queue.length > 0) {
      const current = queue.shift();
      const node = this.cy.$id(current);
      node.neighborhood().nodes().forEach((neighbor) => {
        const nid = neighbor.id();
        if (!visited.has(nid)) {
          visited.add(nid);
          distances[nid] = distances[current] + 1;
          if (distances[nid] > maxDist) maxDist = distances[nid];
          queue.push(nid);
        }
      });
    }
    const maxDegree = this.cy.nodes().max((n) => n.degree()).value || 1;

    if (maxDist <= 1) {
      // When all nodes are one hop from target, use nodeType as primary
      // tier and degree as secondary to create multiple meaningful rings.
      const NODE_TYPE_TIER = { person: 5, organization: 4, fund: 3, education: 3, engagement: 2, event: 1 };
      layoutOptions.concentric = (node) => {
        const dist = distances[node.id()];
        if (dist === undefined) return 0;
        if (dist === 0) return 10000; // target = center
        const nt = (node.data('type') || node.data('nodeType') || '').toLowerCase();
        const tier = NODE_TYPE_TIER[nt] || 1;
        return tier * (maxDegree + 1) + node.degree();
      };
      layoutOptions.levelWidth = () => Math.max(1, Math.ceil(maxDegree / 3));
      layoutOptions.equidistant = true;
      layoutOptions.minNodeSpacing = 50;
    } else {
      // Multi-hop case: BFS distance as primary, degree as secondary
      layoutOptions.concentric = (node) => {
        const dist = distances[node.id()];
        if (dist === undefined) return 0;
        if (dist === 0) return (maxDist + 1) * 1000 + maxDegree + 1;
        return (maxDist - dist) * 1000 + node.degree();
      };
      layoutOptions.levelWidth = () => 2;
    }
    debugLog('[GraphAdapter] BFS injected: maxDist', maxDist, 'visited', visited.size, 'of', this.cy.nodes().length, 'nodes, maxDegree', maxDegree);
  }

  /**
   * Run the layout on the main thread (original behaviour). Used as a fallback
   * when the worker is unavailable or for unsupported layouts like cola.
   */
  _runLayoutOnMainThread(layoutOptions, onComplete) {
    const layout = this.cy.layout(layoutOptions);
    this.currentLayout = layout;
    layout.one('layoutstop', () => {
      this.currentLayout = null;
      this.initialLayoutDone = true;
      onComplete?.();
    });
    layout.run();
    return layout;
  }

  /**
   * Offload layout computation to the Web Worker. Serializes the current
   * graph, sends it to the worker, and applies the result when ready.
   *
   * @returns {Object} A layout-like handle with a `stop()` method
   */
  _runLayoutInWorker(worker, layoutOptions, isIncremental, onComplete, meta) {
    const generation = ++this._layoutGeneration;

    // Phase 12 — Persistent worker. We post a delta against the previously-
    // sent state. The worker applies it to its own headless cy in place and
    // sends back position deltas.
    const delta = this._serializeDelta();

    // Strip function-valued and DOM-related props that cannot be cloned
    const cleanOptions = {};
    for (const key of Object.keys(layoutOptions)) {
      const val = layoutOptions[key];
      if (typeof val !== 'function' && typeof val !== 'object') {
        cleanOptions[key] = val;
      } else if (val === null || val === undefined) {
        cleanOptions[key] = val;
      }
      // Functions (concentric, levelWidth, stop, ready) are re-created inside the worker
    }

    // Determine whether we want to animate position application
    const wantAnimate = layoutOptions.animate !== false;
    const animDuration = layoutOptions.animationDuration || 400;

    // Set up the one-shot message handler for this generation
    const handler = (event) => {
      const msg = event.data;
      if (!msg || msg.generation !== generation) return; // stale result

      worker.removeEventListener('message', handler);

      if (msg.type === 'layout-result') {
        if (msg.timing) {
          perfCount('cy.layout.worker.totalMs', msg.timing.layoutMs | 0);
          if (msg.timing.changedCount != null) {
            perfCount('cy.layout.worker.positionsReturned', msg.timing.changedCount);
          }
        }
        this._applyWorkerPositions(msg.positions, wantAnimate, animDuration, meta).then(() => {
          this.currentLayout = null;
          this.initialLayoutDone = true;
          onComplete?.();
        });
      } else if (msg.type === 'layout-error') {
        console.warn('[GraphAdapter] Worker layout error, falling back to main thread:', msg.error);
        // Reset persistent state on error so the next run sends the full
        // graph fresh, in case the worker's headless cy is corrupt.
        this._workerState.clear();
        worker.postMessage({ type: 'reset' });
        worker.removeEventListener('message', handler);
        this._runLayoutOnMainThread(layoutOptions, onComplete);
      }
    };
    worker.addEventListener('message', handler);

    // Send work to worker — persistent + delta path.
    const message = {
      type: 'run-layout',
      persistent: true,
      delta: {
        added: delta.added,
        updated: delta.updated,
        removedIds: delta.removedIds,
      },
      options: cleanOptions,
      generation,
      targetNodeId: meta?.targetNodeId || null,
      sparseGraph: meta?._sparseGraph || false,
      // Forward the perfLog gate so the worker can quiet its own logs.
      perfLog: isPerfLogEnabled(),
    };

    try {
      worker.postMessage(message);
    } catch (error) {
      console.warn('[GraphAdapter] Worker layout message could not be cloned, falling back to main thread:', error);
      worker.removeEventListener('message', handler);
      this._workerState.clear();
      try { worker.postMessage({ type: 'reset' }); } catch (e) { /* ignore */ }
      return this._runLayoutOnMainThread(layoutOptions, onComplete);
    }

    // Return a handle that lets callers cancel / identify the running layout
    const handle = {
      stop: () => {
        worker.removeEventListener('message', handler);
        // Bump generation so worker discards its result
        this._layoutGeneration++;
        worker.postMessage({ type: 'cancel', generation: this._layoutGeneration });
        this.currentLayout = null;
        onComplete?.();
      },
    };
    this.currentLayout = handle;
    return handle;
  }

  batchUpdate(fn) {
    if (!this.cy) return;
    this.cy.startBatch();
    try { fn(this.cy); }
    finally { this.cy.endBatch(); }
  }

  // ── Viewport ───────────────────────────────────────────────────────

  fit(nodeIds, padding = 50) {
    if (!this.cy) return;
    if (nodeIds && nodeIds.length > 0) {
      const collection = this.cy.collection();
      nodeIds.forEach(id => {
        const el = this.cy.$id(id);
        if (el.length) collection.merge(el);
      });
      if (collection.length > 0) {
        this.cy.fit(collection, padding);
      }
    } else {
      this.cy.fit(undefined, padding);
    }
  }

  zoom(level) {
    if (!this.cy) return 1;
    if (level !== undefined) {
      this.cy.zoom(level);
    }
    return this.cy.zoom();
  }

  pan(position) {
    if (!this.cy) return ORIGIN;
    if (position) {
      this.cy.pan(position);
    }
    return this.cy.pan();
  }

  // ── Coordinate transforms ──────────────────────────────────────────

  renderedPosition(nodeId) {
    const pos = this.cy?.$id(nodeId)?.renderedPosition();
    return pos ? { x: pos.x, y: pos.y } : ORIGIN;
  }

  modelToRenderedPosition(pos) {
    if (!this.cy) return ORIGIN;
    const zoom = this.cy.zoom();
    const pan = this.cy.pan();
    return {
      x: pos.x * zoom + pan.x,
      y: pos.y * zoom + pan.y,
    };
  }

  // ── Node operations ────────────────────────────────────────────────

  ping(nodeId, duration = 1500) {
    if (!this.cy) return;
    const node = this.cy.$id(nodeId);
    if (node.length > 0) {
      node.flashClass('highlighted', duration);
    }
  }

  getPositions() {
    if (!this.cy) return {};
    const positions = {};
    this.cy.nodes().forEach(node => {
      positions[node.id()] = node.position();
    });
    return positions;
  }

  // ── Export ─────────────────────────────────────────────────────────

  exportPng(options = {}) {
    if (!this.cy) return Promise.resolve('');
    const nodeCount = this.cy.nodes().length;
    // Reduce scale for large graphs to prevent memory issues
    const scale = nodeCount > 500 ? 1 : 2;
    const dataUri = this.cy.png({
      full: true,
      scale,
      bg: '#ffffff',
      ...options,
    });
    return Promise.resolve(dataUri);
  }

  exportJson() {
    if (!this.cy) return {};
    return this.cy.json();
  }

  // ── Combos (compound nodes) ────────────────────────────────────────

  collapseCombo(comboId) {
    if (!this.ecApi) return;
    const node = this.cy.$id(comboId);
    if (node.length > 0) {
      this.ecApi.collapse(node);
    }
  }

  expandCombo(comboId) {
    if (!this.ecApi) return;
    const node = this.cy.$id(comboId);
    if (node.length > 0) {
      this.ecApi.expand(node);
    }
  }

  isComboExpanded(comboId) {
    if (!this.ecApi) return true;
    const node = this.cy.$id(comboId);
    if (node.length === 0) return true;
    // In expand-collapse API, "collapsible" means currently expanded.
    if (this.ecApi.isCollapsible(node)) return true;
    if (this.ecApi.isExpandable(node)) return false;
    return true;
  }

  isComboToggleable(comboId) {
    if (!this.ecApi) return false;
    const node = this.cy.$id(comboId);
    if (node.length === 0) return false;
    return node.isParent() || this.ecApi.isCollapsible(node) || this.ecApi.isExpandable(node);
  }

  // ── Graph analysis ─────────────────────────────────────────────────

  dijkstra(rootId) {
    const EMPTY = { distanceTo: () => Infinity, pathTo: () => [] };
    if (!this.cy) return EMPTY;
    const root = this.cy.$id(rootId);
    if (root.length === 0) return EMPTY;
    const result = this.cy.elements().dijkstra({ root });
    return {
      distanceTo: (targetId) => {
        const target = this.cy.$id(targetId);
        return target.length > 0 ? result.distanceTo(target) : Infinity;
      },
      pathTo: (targetId) => {
        const target = this.cy.$id(targetId);
        if (target.length === 0) return [];
        return result.pathTo(target).map(el => el.id());
      },
    };
  }

  shortestPath(sourceId, targetId) {
    if (!this.cy) return [];
    const source = this.cy.$id(sourceId);
    const target = this.cy.$id(targetId);
    if (source.length === 0 || target.length === 0) return [];
    const result = this.cy.elements().aStar({ root: source, goal: target });
    if (!result.found) return [];
    return result.path.map(el => el.id());
  }

  // ── Events ─────────────────────────────────────────────────────────

  on(event, selector, handler) {
    if (!this.cy) return;
    if (selector) {
      this.cy.on(event, selector, handler);
    } else {
      this.cy.on(event, handler);
    }
  }

  off(event, selector, handler) {
    if (!this.cy) return;
    if (selector) {
      this.cy.off(event, selector, handler);
    } else {
      this.cy.off(event, handler);
    }
  }

  // ── Direct access (escape hatch) ──────────────────────────────────

  getCy() {
    return this.cy;
  }
}

export default GraphAdapter;
